import {
  BANNED_MINT_LANGUAGE_CODES,
  Locale,
  LocaleDetails,
  countryLanguages,
  supportedLocales,
} from 'shared/src/types/languages.type';

/**
 * Where the language menu should send a shopper who picks another language.
 *
 * The menu links to the page's twin URL from `data.href`. That URL is only a
 * promise: `/fr-CA/electronique-informatique/technologie-portable/aides-auditives`
 * has a mapping but its French document was never written, so the page answers
 * 404 ("Cette page n'est pas disponible"). This decides, with a few read-only
 * lookups, whether the twin resolves and, when it does not, falls back to the
 * nearest live ancestor in that language, then to the locale home.
 *
 * It mirrors what `fetchWithFallback` (server/fetch-document) does with the URL:
 * - The language has a mapping for the URL: it resolves when the document the
 *   mapping points at exists. A mapping without its document is an orphan:
 *   since 2026-09-28 a shopper's visit translates it again from its source
 *   (the "translating your page" board) when the source language still lists
 *   the URL, so it resolves then; otherwise it 404s.
 * - The language has no mapping yet, in the page's order: the same path in a
 *   sibling language answers; else the visit mints the translation when the
 *   region's source language lists the URL (since 2026-09-28 before the parent
 *   fallback, which used to answer 404 first for three slugs or more).
 * Ancestors only count when they are real pages (mapping plus document): a
 * fallback should land on content, never start a page build.
 */

/** One URL mapping as the resolver needs it: the category ref it points at. */
export type LanguageSwitchMapping = { ref?: string | null } | null;

export type LanguageSwitchLookups = {
  /** `getUrlMapping`: the mapping `locale` has for `url` (rows of `targetLang` when given). */
  findMapping: (locale: LocaleDetails, url: string, targetLang?: Locale) => Promise<LanguageSwitchMapping>;
  /** Whether the category row a mapping's `ref` points at exists. */
  documentExists: (ref: string) => Promise<boolean>;
};

/** The menu never asks about more URLs than this in one call. */
export const MAX_LANGUAGE_SWITCH_TARGETS = 6;
/** Longer than any page URL we mint; anything past it is not ours. */
export const MAX_LANGUAGE_SWITCH_URL_LENGTH = 600;
/**
 * Deeper than any page path we mint. The resolver probes every ancestor (two
 * reads each), and the server action is a public endpoint: without a cap one
 * call of six 600-character `/a/a/a/...` URLs asked the database about 3,600
 * times. A deeper URL is left as it is, like any other URL that is not ours.
 */
export const MAX_LANGUAGE_SWITCH_DEPTH = 10;
/** The same cap fetchWithFallback puts on sibling-language probes. */
const MAX_SIBLING_LOCALES = 3;

type ParsedTarget = {
  locale: LocaleDetails;
  /** `/fr-CA` */
  home: string;
  /** Path segments after the locale. */
  slugs: string[];
};

/** A same-site page path whose first segment is a locale we serve, else null. */
export function parseLanguageSwitchTarget(url: unknown): ParsedTarget | null {
  if (typeof url !== 'string') return null;
  if (url.length === 0 || url.length > MAX_LANGUAGE_SWITCH_URL_LENGTH) return null;
  if (!url.startsWith('/') || url.startsWith('//')) return null;
  if (/[?#]/.test(url)) return null;
  const [localePath, ...slugs] = url.split('/').filter(Boolean);
  const locale = localePath ? countryLanguages[localePath as Locale] : undefined;
  if (!locale) return null;
  if (slugs.length > MAX_LANGUAGE_SWITCH_DEPTH) return null;
  return { locale, home: `/${locale.path}`, slugs };
}

/** Parents of the target in its own language, nearest first, locale home excluded. */
export function languageSwitchAncestors(url: string): string[] {
  const parsed = parseLanguageSwitchTarget(url);
  if (!parsed) return [];
  const ancestors: string[] = [];
  for (let depth = parsed.slugs.length - 1; depth >= 1; depth -= 1) {
    ancestors.push(`${parsed.home}/${parsed.slugs.slice(0, depth).join('/')}`);
  }
  return ancestors;
}

async function isLivePage(
  locale: LocaleDetails,
  url: string,
  lookups: LanguageSwitchLookups,
): Promise<boolean> {
  const mapping = await lookups.findMapping(locale, url);
  return Boolean(mapping?.ref) && (await lookups.documentExists(mapping!.ref!));
}

/** The region's source language lists `url` in a live page's hrefs: a visit can translate it from there. */
async function sourceListsTwin(
  locale: LocaleDetails,
  url: string,
  lookups: LanguageSwitchLookups,
): Promise<boolean> {
  const sourceLocale = locale.default ? countryLanguages[locale.default] : undefined;
  if (!sourceLocale || sourceLocale.path === locale.path) return false;
  const source = await lookups.findMapping(sourceLocale, url, locale.path);
  return Boolean(source?.ref) && (await lookups.documentExists(source!.ref!));
}

/** True when a visit to `url` answers with a page (existing, minted or redirected), not a 404. */
async function twinResolves(
  parsed: ParsedTarget,
  url: string,
  lookups: LanguageSwitchLookups,
): Promise<boolean> {
  const { locale, slugs } = parsed;
  const mapping = await lookups.findMapping(locale, url);
  if (mapping) {
    if (!mapping.ref) return false; // no document to point at: the page 404s
    if (await lookups.documentExists(mapping.ref)) return true;
    // Orphan mapping (its document is gone): a shopper's visit translates the
    // twin again from its source and shows the "translating your page"
    // board (fetch-document-by-url, findTwinSource), when the source language
    // still lists this URL and the language is one we mint. Otherwise the page
    // falls back to the parent and 404s.
    if (BANNED_MINT_LANGUAGE_CODES.includes(locale.languageCode)) return false;
    return sourceListsTwin(locale, url, lookups);
  }

  // 1. The same path in a sibling language answers (301, or the translating
  //    board on an in-site visit).
  const pathWithoutLocale = slugs.join('/');
  const siblings = Array.from(
    new Set(
      [locale.default as string | undefined, ...(supportedLocales[locale.path] ?? []).map((sibling) => sibling.path as string)]
        .filter((path): path is string => !!path && path !== locale.path),
    ),
  ).slice(0, MAX_SIBLING_LOCALES);
  const siblingAnswers = await Promise.all(
    siblings.map((siblingPath) => {
      const sibling = countryLanguages[siblingPath as Locale];
      return sibling
        ? lookups.findMapping(sibling, `/${sibling.path}/${pathWithoutLocale}`).then(Boolean)
        : Promise.resolve(false);
    }),
  );
  if (siblingAnswers.some(Boolean)) return true;

  // 2. The visit mints the translation when the source language lists this
  //    URL (createTranslations' first lookup). Three slugs or more: a
  //    shopper's visit mints it before the parent fallback (findTwinSource,
  //    which skips the languages we never mint); until 2026-09-28 the parent
  //    answered 404 first.
  if (slugs.length >= 3 && BANNED_MINT_LANGUAGE_CODES.includes(locale.languageCode)) return false;
  return sourceListsTwin(locale, url, lookups);
}

/**
 * The URL to send a shopper to for `url`: the twin itself when it resolves,
 * else its nearest live ancestor in the same language, else the locale home.
 * A URL that is not one of our page paths comes back unchanged.
 */
export async function resolveLanguageSwitchTarget(
  url: string,
  lookups: LanguageSwitchLookups,
): Promise<string> {
  const parsed = parseLanguageSwitchTarget(url);
  if (!parsed) return url;
  // The locale home always answers.
  if (parsed.slugs.length === 0) return url;

  if (await twinResolves(parsed, url, lookups)) return url;

  const ancestors = languageSwitchAncestors(url);
  const live = await Promise.all(ancestors.map((ancestor) => isLivePage(parsed.locale, ancestor, lookups)));
  const nearest = ancestors.find((_, index) => live[index]);
  return nearest ?? parsed.home;
}

/**
 * The menu's side of a click: wait for the resolver, but never longer than
 * `timeoutMs`, and never let a failed lookup block the switch. On a timeout or
 * an error the shopper goes to the twin URL, as before this check existed.
 */
export async function settleLanguageSwitchTarget(
  url: string,
  resolve: (url: string) => Promise<string | undefined>,
  timeoutMs: number,
): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((done) => {
    timer = setTimeout(() => done(undefined), timeoutMs);
  });
  try {
    const resolved = await Promise.race([resolve(url).catch(() => undefined), timeout]);
    return typeof resolved === 'string' && resolved.startsWith('/') ? resolved : url;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
